/**
 * Rebase-rebuild on non-fast-forward.
 *
 * The brain repo is a shared single-trunk store: many workers' dreamers commit
 * to `main`. When our optimistic `commitBatch(expectedOldSha)` loses the race a
 * concurrent push wins and we get `non-fast-forward`. We MUST NOT replay our
 * batch from a stale base — that would clobber the concurrently-added index line
 * (or any concurrently-added file). Instead we re-fetch the tip, re-read
 * `MEMORY.md`, and rebuild the index by *merging* our prepended line onto the
 * CURRENT index (header + concurrent lines preserved), then retry.
 *
 * After a successful update-ref we run an END-STATE verify (re-read the repo) —
 * never a commit-count check. A failed verify yields `repair` (the caller must
 * reconcile); exhausting `maxAttempts` of non-fast-forward yields `deferred`.
 */
import type { BrainRepoWriter, FileChange } from "./types";

export interface IndexEdit {
  path: string;          // "memory/MEMORY.md"
  header: string;        // preserved verbatim at the top
  prependLine: string;   // our newest-first index line(s) (may be empty — e.g. a pure retract)
  /**
   * Memory paths whose index line this batch RETIRES (supersede oldPath, retract
   * path). mergeIndex drops any live line referencing one of these before
   * prepending, so the merge is a (prepend-set − remove-set) reconciliation
   * against the live tip — not prepend-only. Empty for a pure `new`.
   */
  removePaths?: string[];
}

export interface CommitWithRebaseArgs {
  writer: BrainRepoWriter;
  message: string;
  changes: FileChange[];                 // the non-index file changes (memory/inbox/quarantine)
  indexEdit?: IndexEdit;                 // merged fresh onto each attempt's MEMORY.md
  maxAttempts: number;
  /** End-STATE check (re-read the repo); NEVER a commit-count check. */
  verify: (newSha: string) => Promise<boolean>;
}

export type CommitWithRebaseResult =
  | { outcome: "committed"; newSha: string; attempts: number }
  | { outcome: "repair"; newSha: string; attempts: number }
  | { outcome: "deferred"; attempts: number; reason: "non-fast-forward-exhausted" | "error"; error?: string };

/**
 * Reconcile our index edit onto the live MEMORY.md: drop every live line that
 * references a retired path (supersede oldPath / retract path), then prepend our
 * newest-first line(s) under the header — preserving every other existing
 * (possibly concurrently-added) line. A (prepend-set − remove-set) merge, not
 * prepend-only, so a superseded/retracted file is actually removed from the index
 * and a pure de-index (empty prependLine) never injects a stray line.
 */
function mergeIndex(current: string | null, edit: IndexEdit): string {
  const body = current?.startsWith(edit.header)
    ? current.slice(edit.header.length)
    : (current ?? "").replace(/^#[^\n]*\n+/, "");
  const remove = edit.removePaths ?? [];
  const kept = body
    .split("\n")
    .filter((l) => !remove.some((p) => l.includes(`\`${p}\``)))
    .join("\n")
    .replace(/^\n+/, "");
  const prepend = edit.prependLine ? `${edit.prependLine}\n` : "";
  return `${edit.header}${prepend}${kept}`.replace(/\n+$/, "\n");
}

export async function commitWithRebase(a: CommitWithRebaseArgs): Promise<CommitWithRebaseResult> {
  let attempts = 0;
  let lastError: string | undefined;
  while (attempts < a.maxAttempts) {
    attempts += 1;
    let tip: string;
    try {
      tip = await a.writer.fetchTip();
    } catch (e) {
      lastError = e instanceof Error ? e.message : String(e);
      continue;
    }
    // Rebuild the index fresh on top of THIS tip's MEMORY.md (merge, never stale-base replace).
    let changes = a.changes;
    if (a.indexEdit) {
      const indexEdit = a.indexEdit;
      const currentIndex = await a.writer.readFile(indexEdit.path);
      changes = [
        ...a.changes.filter((c) => c.path !== indexEdit.path),
        { path: indexEdit.path, content: mergeIndex(currentIndex, indexEdit) },
      ];
    }
    const res = await a.writer.commitBatch({ expectedOldSha: tip, message: a.message, changes });
    if (res.ok && res.newSha) {
      const landed = await a.verify(res.newSha);
      return landed
        ? { outcome: "committed", newSha: res.newSha, attempts }
        : { outcome: "repair", newSha: res.newSha, attempts };
    }
    if (res.reason === "non-fast-forward") continue; // re-fetch + rebuild + retry
    return { outcome: "deferred", attempts, reason: "error", error: res.error };
  }
  return { outcome: "deferred", attempts, reason: lastError ? "error" : "non-fast-forward-exhausted", ...(lastError ? { error: lastError } : {}) };
}

/** End-state verifier: re-read the changed files and confirm the intended content landed. */
export async function verifyEndState(writer: BrainRepoWriter, changes: FileChange[]): Promise<boolean> {
  for (const c of changes) {
    const actual = await writer.readFile(c.path);
    if (actual !== c.content) return false;
  }
  return true;
}
